import { useLocation } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { countPendingDocuments } from '@/db/services/document_service';
import { AuthExpiredError } from '@/lib/fetch-utils';
import { useBanner } from '@/hooks/useBanner';
import { useCurrentPerson } from '@/hooks/useCurrentPerson';
import { useServerOnline } from '@/hooks/useServerOnline';
import { useSyncActivity } from '@/hooks/useSyncActivity';
import { isAuthRoute, unsyncedWorkMessage } from './messages';

/**
 * Posts the "unsynced-work" message: a failed sync on any page, and documents
 * waiting to upload on the dashboard (see unsyncedWorkMessage). Documents are
 * counted on the dashboard only, so editor autosaves never flash the banner.
 */
export function useUnsyncedWorkBanner(): void {
  const { organizationId } = useCurrentPerson();
  const online = useServerOnline();
  const activity = useSyncActivity();
  const { pathname } = useLocation();
  const onDashboard = pathname === '/';
  const signedIn = organizationId !== null && !isAuthRoute(pathname);

  const countPending = signedIn && onDashboard;
  const pendingCount = useLiveQuery(
    async () => (countPending ? countPendingDocuments(organizationId!) : 0),
    [countPending, organizationId],
  ) ?? 0;

  useBanner(
    signedIn
      ? unsyncedWorkMessage({
          pendingCount,
          online,
          onDashboard,
          syncFailed: activity.status === 'error' && !(activity.lastError instanceof AuthExpiredError),
          failedAt: activity.finishedAt,
          syncingVisibly: activity.status === 'syncing' && activity.visible,
        })
      : null,
  );
}
